import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface IceSportSpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  rpm?: number;
  gear?: number;
  acceleration: number;
  maxSpeed?: number;
}

export const IceSportSpeedometer: React.FC<IceSportSpeedometerProps> = ({
  speed,
  speedUnit,
  rpm = 3200,
  gear = 3,
  acceleration,
  maxSpeed = 280,
}) => {
  const arcRef = useRef<SVGCircleElement | null>(null);
  const accelSurgeRef = useRef<HTMLDivElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  const radius = 125;
  const circumference = 2 * Math.PI * radius;
  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const progressRatio = clampedSpeed / maxSpeed;
  const strokeDashoffset = circumference * (1 - progressRatio * 0.80);

  useEffect(() => {
    if (!arcRef.current) return;
    anime({
      targets: arcRef.current,
      strokeDashoffset,
      duration: 350,
      easing: 'easeOutCubic',
    });
  }, [strokeDashoffset]);

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 300,
      easing: 'easeOutCubic',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  const isHighRev = (rpm || 0) > 6000;

  return (
    <div className="relative w-76 h-76 md:w-88 md:h-88 flex flex-col items-center justify-center select-none">
      {/* Redline / High-Glow Ambient Aura */}
      <div
        ref={accelSurgeRef}
        className={`absolute inset-0 rounded-full blur-3xl pointer-events-none transition-all duration-200 ${
          isHighRev ? 'bg-red-600/35 scale-110' : 'bg-rose-600/20'
        }`}
      />

      {/* SVG Angular Track & Sport Arcs */}
      <svg className="w-full h-full transform -rotate-140" viewBox="0 0 300 300">
        {/* Background Arc */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="rgba(244, 63, 94, 0.15)"
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * 0.20}
          strokeLinecap="round"
        />

        {/* Dynamic Sport Acceleration Arc */}
        <circle
          ref={arcRef}
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke={isHighRev ? '#ef4444' : '#f43f5e'}
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          className="filter drop-shadow-[0_0_12px_rgba(244,63,94,0.7)]"
        />
      </svg>

      {/* Center Tachometer / Performance Readout */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {/* Gear Badge */}
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2.5 py-0.5 rounded bg-rose-950/80 border border-rose-500/60 text-xs font-mono font-black text-rose-300">
            GEAR {gear || 1}
          </span>
          <span className="text-[10px] font-mono font-bold tracking-widest text-slate-400">
            {Math.round(rpm || 0)} RPM
          </span>
        </div>

        {/* Speed */}
        <span
          ref={speedTextRef}
          className="text-7xl md:text-8xl font-black font-display text-white tracking-tighter drop-shadow-[0_4px_16px_rgba(244,63,94,0.5)] leading-none my-1"
        >
          {Math.round(speed)}
        </span>
        <span className="text-xs font-mono tracking-widest text-rose-400 font-black uppercase">
          {speedUnit.toUpperCase()}
        </span>

        {/* G-Force / Acceleration Meter */}
        <div className="flex items-center gap-1.5 mt-3">
          <span className="text-[9px] font-mono font-bold tracking-wider text-rose-500/80">
            THRUST
          </span>
          <div className="w-24 h-1.5 bg-slate-900 rounded-full overflow-hidden border border-rose-900/60">
            <div
              className="h-full bg-gradient-to-r from-rose-500 to-amber-400 rounded-full transition-all duration-150"
              style={{ width: `${Math.max(8, Math.min(100, (acceleration + 0.5) * 100))}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
